import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import { loadCurrentUser } from "@/app/stores/auth/auth.actions";
import { selectorCurrentUser, selectorCurrentUserStatus } from "@/app/stores/auth/auth.selectors";
import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { AuthService } from "@auth0/auth0-angular";
import { resolveClientOnboardingSource } from "@/app/core/utils/case-workflow.util";

export const profileGuard: CanActivateFn = (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const auth = inject(AuthService);

  return auth.isAuthenticated$.pipe(
    filter((isAuth) => isAuth !== null),
    take(1),
    switchMap((isAuthenticated) => {
      // Not authenticated — let AuthGuard handle it
      if (!isAuthenticated) return of(true);

      return store.select(selectorCurrentUserStatus).pipe(
        take(1),
        switchMap((status) => {
          // Dispatch load if not yet fetched
          if (status === ELoadingStatus.idle) {
            store.dispatch(loadCurrentUser());
          }

          // Wait until loaded or errored
          return store.select(selectorCurrentUserStatus).pipe(
            filter((s) => s === ELoadingStatus.loaded || s === ELoadingStatus.error),
            take(1),
            switchMap(() => store.select(selectorCurrentUser).pipe(take(1)))
          );
        }),
        map((user) => {
          // No firstName → force onboarding, preserve current URL so welcome can redirect back after
          if (!user?.firstName) {
            const source = resolveClientOnboardingSource(user?.id?.startsWith("google-oauth2|") ? "google" : "auth0");
            localStorage.setItem("post_onboard_redirect", state.url);
            return router.createUrlTree(["/onboarding"], { queryParams: { source } });
          }

          // Has firstName but email not verified → force verification-sent
          if (!user?.emailVerified) {
            return router.createUrlTree(["/verification-sent"], {
              queryParams: { email: user.email },
            });
          }

          return true;
        })
      );
    })
  );
};
